import * as Y from 'yjs'

import type {
  DocumentSessionBootstrap,
  DocumentSessionCommittedUpdate,
} from '../protocol/documentSessionProtocol'
import {
  SPREADSHEET_ENGINE_OPS_DOC_MAP,
  SPREADSHEET_WORKBOOK_SNAPSHOT_DOC_MAP,
  isSpreadsheetEngineOperationEnvelope,
  type SpreadsheetEngineOperationEnvelope,
  isSpreadsheetWorkbookSnapshot,
  type SpreadsheetWorkbookSnapshot,
} from '../protocol/spreadsheetEngineProtocol'
import {
  SPREADSHEET_EXCEL_IO_WARNING_HISTORY_DOC_MAP,
  SPREADSHEET_EXCEL_IO_WARNING_HISTORY_LIMIT,
  normalizeSpreadsheetExcelIoWarningHistoryEntries,
  type SpreadsheetExcelIoWarningHistoryEntry,
} from '../protocol/spreadsheetExcelUnsupportedArtifacts'

export type DocumentSessionMaterializedState = {
  root_payload_b64: string | null
  chunk_payloads_b64: Record<string, string>
}

const encodeBytesToBase64 = (bytes: Uint8Array): string => {
  if (typeof Buffer !== 'undefined') return Buffer.from(bytes).toString('base64')
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

const decodeBase64ToBytes = (payloadB64Raw: string): Uint8Array => {
  const payloadB64 = String(payloadB64Raw || '').trim()
  if (!payloadB64) return new Uint8Array()
  if (typeof Buffer !== 'undefined') return Uint8Array.from(Buffer.from(payloadB64, 'base64'))
  const binary = atob(payloadB64)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i)
  return out
}

export class DocumentSessionReplica {
  private rootDoc = new Y.Doc()
  private chunkDocs = new Map<string, Y.Doc>()
  private hasRootState = false
  private chunkIdsWithState = new Set<string>()

  reset(): void {
    try { this.rootDoc.destroy() } catch { }
    for (const doc of this.chunkDocs.values()) {
      try { doc.destroy() } catch { }
    }
    this.rootDoc = new Y.Doc()
    this.chunkDocs = new Map()
    this.hasRootState = false
    this.chunkIdsWithState = new Set()
  }

  hydrateFromBootstrap(
    bootstrap: Pick<DocumentSessionBootstrap, 'snapshot_payload_b64' | 'chunk_snapshot_payloads_b64' | 'tail'>,
  ): DocumentSessionMaterializedState {
    this.reset()
    this.applyRootPayload(bootstrap?.snapshot_payload_b64 ?? null)
    const chunkPayloads = bootstrap?.chunk_snapshot_payloads_b64 && typeof bootstrap.chunk_snapshot_payloads_b64 === 'object'
      ? bootstrap.chunk_snapshot_payloads_b64
      : {}
    for (const [chunkId, payloadB64] of Object.entries(chunkPayloads)) {
      this.applyChunkPayload(chunkId, payloadB64)
    }
    this.applyCommittedUpdates(Array.isArray(bootstrap?.tail) ? bootstrap.tail : [])
    return this.getMaterializedState()
  }

  hydrateFromPersistedState(state: {
    root_payload_b64?: string | null
    chunk_payloads_b64?: Record<string, string> | null
  } | null | undefined): DocumentSessionMaterializedState {
    this.reset()
    this.applyRootPayload(state?.root_payload_b64 ?? null)
    const chunkPayloads = state?.chunk_payloads_b64 && typeof state.chunk_payloads_b64 === 'object'
      ? state.chunk_payloads_b64
      : {}
    for (const [chunkId, payloadB64] of Object.entries(chunkPayloads)) {
      this.applyChunkPayload(chunkId, payloadB64)
    }
    return this.getMaterializedState()
  }

  applyCommittedUpdates(updates: DocumentSessionCommittedUpdate[]): DocumentSessionMaterializedState {
    for (const update of Array.isArray(updates) ? updates : []) {
      const docScope = String(update?.doc_scope || '').trim()
      if (docScope === 'workbook_root') {
        this.applyRootPayload(update?.update_payload_b64 ?? null)
        continue
      }
      if (docScope === 'sheet_chunk') {
        this.applyChunkPayload(update?.chunk_id ?? null, update?.update_payload_b64 ?? null)
      }
    }
    return this.getMaterializedState()
  }

  buildWorkbookRootUpdate(mutate: (doc: Y.Doc) => void): string | null {
    return this.captureDocUpdate(this.rootDoc, mutate, () => {
      this.hasRootState = true
    })
  }

  buildSheetChunkUpdate(chunkIdRaw: string, mutate: (doc: Y.Doc) => void): string | null {
    const chunkId = String(chunkIdRaw || '').trim()
    if (!chunkId) return null
    const doc = this.chunkDocs.get(chunkId) || new Y.Doc()
    const payload = this.captureDocUpdate(doc, mutate, () => {
      this.chunkDocs.set(chunkId, doc)
      this.chunkIdsWithState.add(chunkId)
    })
    return payload
  }

  appendSheetChunkSpreadsheetEngineOps(
    chunkIdRaw: string,
    operations: SpreadsheetEngineOperationEnvelope[],
  ): string | null {
    const engineOps = (Array.isArray(operations) ? operations : [])
      .filter((operation) => String(operation?.op_id || '').trim())
    if (!engineOps.length) return null
    return this.buildSheetChunkUpdate(chunkIdRaw, (doc) => {
      const opsMap = doc.getMap(SPREADSHEET_ENGINE_OPS_DOC_MAP)
      for (const operation of engineOps) {
        opsMap.set(String(operation.op_id).trim(), operation)
      }
    })
  }

  appendWorkbookRootSpreadsheetEngineOps(
    operations: SpreadsheetEngineOperationEnvelope[],
  ): string | null {
    const engineOps = (Array.isArray(operations) ? operations : [])
      .filter((operation) => String(operation?.op_id || '').trim())
    if (!engineOps.length) return null
    return this.buildWorkbookRootUpdate((doc) => {
      const opsMap = doc.getMap(SPREADSHEET_ENGINE_OPS_DOC_MAP)
      for (const operation of engineOps) {
        opsMap.set(String(operation.op_id).trim(), operation)
      }
    })
  }

  replaceSpreadsheetWorkbookSnapshot(snapshot: SpreadsheetWorkbookSnapshot): string | null {
    if (!isSpreadsheetWorkbookSnapshot(snapshot)) return null
    this.reset()
    return this.buildWorkbookRootUpdate((doc) => {
      const snapshotMap = doc.getMap(SPREADSHEET_WORKBOOK_SNAPSHOT_DOC_MAP)
      snapshotMap.set('snapshot', snapshot)
      snapshotMap.set('workbook_id', snapshot.workbook_id)
      snapshotMap.set('workbook_revision', snapshot.workbook_revision)
      snapshotMap.set('replaced_at_ms', Date.now())
    })
  }

  appendSpreadsheetExcelIoWarningHistoryEntry(
    entry: SpreadsheetExcelIoWarningHistoryEntry | null | undefined,
  ): string | null {
    const normalizedEntry = normalizeSpreadsheetExcelIoWarningHistoryEntries(
      entry ? [entry] : [],
      1,
    )[0]
    if (!normalizedEntry) return null
    return this.buildWorkbookRootUpdate((doc) => {
      const warningHistoryMap = doc.getMap(SPREADSHEET_EXCEL_IO_WARNING_HISTORY_DOC_MAP)
      const existing = normalizeSpreadsheetExcelIoWarningHistoryEntries(
        warningHistoryMap.get('entries'),
        SPREADSHEET_EXCEL_IO_WARNING_HISTORY_LIMIT,
      )
      const nextEntries = normalizeSpreadsheetExcelIoWarningHistoryEntries(
        [normalizedEntry, ...existing],
        SPREADSHEET_EXCEL_IO_WARNING_HISTORY_LIMIT,
      )
      warningHistoryMap.set('entries', nextEntries)
      warningHistoryMap.set('updated_at_ms', Date.now())
    })
  }

  getMaterializedState(): DocumentSessionMaterializedState {
    const chunkPayloads: Record<string, string> = {}
    for (const chunkId of this.chunkIdsWithState) {
      const doc = this.chunkDocs.get(chunkId)
      if (!doc) continue
      chunkPayloads[chunkId] = encodeBytesToBase64(Y.encodeStateAsUpdate(doc))
    }
    return {
      root_payload_b64: this.hasRootState ? encodeBytesToBase64(Y.encodeStateAsUpdate(this.rootDoc)) : null,
      chunk_payloads_b64: chunkPayloads,
    }
  }

  readRootMap(mapNameRaw: string): Record<string, unknown> | null {
    const mapName = String(mapNameRaw || '').trim()
    if (!mapName) return null
    const value = this.rootDoc.getMap(mapName).toJSON()
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : null
  }

  readSpreadsheetEngineOperations(): SpreadsheetEngineOperationEnvelope[] {
    const byOpId = new Map<string, SpreadsheetEngineOperationEnvelope>()
    const workbookSnapshotReplacedAtMs = this.readSpreadsheetWorkbookSnapshotReplacementMs()
    const acceptsOperation = (operation: SpreadsheetEngineOperationEnvelope): boolean => (
      workbookSnapshotReplacedAtMs <= 0 ||
      Number(operation?.created_at_ms ?? 0) >= workbookSnapshotReplacedAtMs
    )
    const rootOps = this.rootDoc.getMap(SPREADSHEET_ENGINE_OPS_DOC_MAP).toJSON()
    if (rootOps && typeof rootOps === 'object' && !Array.isArray(rootOps)) {
      for (const [opIdRaw, operation] of Object.entries(rootOps)) {
        if (!isSpreadsheetEngineOperationEnvelope(operation)) continue
        if (!acceptsOperation(operation)) continue
        const opId = String(operation.op_id || opIdRaw || '').trim()
        if (!opId) continue
        byOpId.set(opId, operation)
      }
    }
    const chunkIds = Array.from(this.chunkIdsWithState).sort()
    for (const chunkId of chunkIds) {
      const doc = this.chunkDocs.get(chunkId)
      if (!doc) continue
      const ops = doc.getMap(SPREADSHEET_ENGINE_OPS_DOC_MAP).toJSON()
      if (!ops || typeof ops !== 'object' || Array.isArray(ops)) continue
      for (const [opIdRaw, operation] of Object.entries(ops)) {
        if (!isSpreadsheetEngineOperationEnvelope(operation)) continue
        if (!acceptsOperation(operation)) continue
        const opId = String(operation.op_id || opIdRaw || '').trim()
        if (!opId) continue
        byOpId.set(opId, operation)
      }
    }
    return Array.from(byOpId.values()).sort((a, b) => {
      const createdDelta = Number(a?.created_at_ms || 0) - Number(b?.created_at_ms || 0)
      if (createdDelta !== 0) return createdDelta
      const revisionDelta = Number(a?.base_revision || 0) - Number(b?.base_revision || 0)
      if (revisionDelta !== 0) return revisionDelta
      return String(a?.op_id || '').localeCompare(String(b?.op_id || ''))
    })
  }

  readSpreadsheetWorkbookSnapshot(): SpreadsheetWorkbookSnapshot | null {
    const snapshot = this.rootDoc.getMap(SPREADSHEET_WORKBOOK_SNAPSHOT_DOC_MAP).get('snapshot')
    return isSpreadsheetWorkbookSnapshot(snapshot) ? snapshot : null
  }

  readSpreadsheetWorkbookSnapshotReplacementMs(): number {
    const snapshotMap = this.rootDoc.getMap(SPREADSHEET_WORKBOOK_SNAPSHOT_DOC_MAP)
    const replacedAtMs = Number(snapshotMap.get('replaced_at_ms') ?? 0)
    return Number.isFinite(replacedAtMs) && replacedAtMs > 0 ? replacedAtMs : 0
  }

  readSpreadsheetExcelIoWarningHistory(): SpreadsheetExcelIoWarningHistoryEntry[] {
    const warningHistoryMap = this.rootDoc.getMap(SPREADSHEET_EXCEL_IO_WARNING_HISTORY_DOC_MAP)
    return normalizeSpreadsheetExcelIoWarningHistoryEntries(
      warningHistoryMap.get('entries'),
      SPREADSHEET_EXCEL_IO_WARNING_HISTORY_LIMIT,
    )
  }

  private applyRootPayload(payloadB64Raw: string | null | undefined): void {
    const payloadB64 = String(payloadB64Raw || '').trim()
    if (!payloadB64) return
    try {
      Y.applyUpdate(this.rootDoc, decodeBase64ToBytes(payloadB64))
      this.hasRootState = true
    } catch {
      // Ignore invalid payloads until the server starts sending stronger guarantees.
    }
  }

  private applyChunkPayload(chunkIdRaw: string | null | undefined, payloadB64Raw: string | null | undefined): void {
    const chunkId = String(chunkIdRaw || '').trim()
    const payloadB64 = String(payloadB64Raw || '').trim()
    if (!chunkId || !payloadB64) return
    try {
      const doc = this.chunkDocs.get(chunkId) || new Y.Doc()
      Y.applyUpdate(doc, decodeBase64ToBytes(payloadB64))
      this.chunkDocs.set(chunkId, doc)
      this.chunkIdsWithState.add(chunkId)
    } catch {
      // Ignore invalid payloads until the server starts sending stronger guarantees.
    }
  }

  private captureDocUpdate(
    doc: Y.Doc,
    mutate: (doc: Y.Doc) => void,
    onApplied: () => void,
  ): string | null {
    const capturedUpdates: Uint8Array[] = []
    const handleUpdate = (update: Uint8Array) => {
      capturedUpdates.push(update)
    }
    doc.on('update', handleUpdate)
    try {
      mutate(doc)
    } finally {
      doc.off('update', handleUpdate)
    }
    if (capturedUpdates.length === 0) return null
    const captured = capturedUpdates.length === 1 ? capturedUpdates[0] : Y.mergeUpdates(capturedUpdates)
    if (!captured.length) return null
    onApplied()
    return encodeBytesToBase64(captured)
  }
}
